import { clamp, inRange01, type Point } from '../../../utils/math';
import { type CornerGridGeometry } from './geometry';

const isPointInQuad = (point: Point, quad: Point[]): boolean => {
  const [x, y] = point;
  let inside = false;
  for (let i = 0, j = quad.length - 1; i < quad.length; j = i++) {
    const [xi, yi] = quad[i];
    const [xj, yj] = quad[j];
    if (yi === yj) continue;
    const intersect =
      yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi;
    if (intersect) inside = !inside;
  }
  return inside;
};

export const getSegmentBoundary = (
  grid: CornerGridGeometry,
  segmentIndex: number,
): {
  leftTop: Point;
  leftBottom: Point;
  rightTop: Point;
  rightBottom: Point;
} => {
  const { points, cornerLines } = grid;
  const cornerLinesCount = cornerLines?.length ?? 0;

  const isFirstSegment = segmentIndex === 0;
  const isLastSegment = segmentIndex === cornerLinesCount;

  return {
    leftTop: isFirstSegment ? points[0] : cornerLines![segmentIndex - 1][0],
    leftBottom: isFirstSegment ? points[3] : cornerLines![segmentIndex - 1][1],
    rightTop: isLastSegment ? points[1] : cornerLines![segmentIndex][0],
    rightBottom: isLastSegment ? points[2] : cornerLines![segmentIndex][1],
  };
};

export const getSegmentPoints = (
  grid: CornerGridGeometry,
  segmentIndex: number,
): Point[] => {
  const { leftTop, rightTop, rightBottom, leftBottom } = getSegmentBoundary(
    grid,
    segmentIndex,
  );
  return [leftTop, rightTop, rightBottom, leftBottom];
};

export const getSegmentIndexAtPoint = (
  grid: CornerGridGeometry,
  scenePoint: Point,
): number => {
  const cornerLinesCount = grid.cornerLines?.length ?? 0;
  const segmentCount = cornerLinesCount + 1;

  for (let i = 0; i < segmentCount; i++) {
    const { leftTop, rightTop, rightBottom, leftBottom } = getSegmentBoundary(
      grid,
      i,
    );

    // translate local coords to scene coords
    const quadScene: Point[] = [
      [leftTop[0] + grid.x, leftTop[1] + grid.y],
      [rightTop[0] + grid.x, rightTop[1] + grid.y],
      [rightBottom[0] + grid.x, rightBottom[1] + grid.y],
      [leftBottom[0] + grid.x, leftBottom[1] + grid.y],
    ];

    if (isPointInQuad(scenePoint, quadScene)) return i;
  }

  return -1;
};

/**
 * inverse bilinear interpolation to find a point relative position (0 to 1) within a segment.
 */
export const getPercentageInSegment = (
  segmentIndex: number,
  scenePoint: Point,
  grid: CornerGridGeometry,
): number => {
  // convert scene point to local point
  const localPoint: Point = [scenePoint[0] - grid.x, scenePoint[1] - grid.y];
  const { leftTop, rightTop, rightBottom, leftBottom } = getSegmentBoundary(
    grid,
    segmentIndex,
  );

  // calculate width (dx) and height (dy) vectors for the top and bottom edges
  const dxTop = rightTop[0] - leftTop[0];
  const dyTop = rightTop[1] - leftTop[1];
  const dxBot = rightBottom[0] - leftBottom[0];
  const dyBot = rightBottom[1] - leftBottom[1];

  // calculate mid points for the top and bottom edges
  const dxMid = dxBot - dxTop;
  const dyMid = dyBot - dyTop;

  // left edge vector
  const dxLeft = leftBottom[0] - leftTop[0];
  const dyLeft = leftBottom[1] - leftTop[1];

  // vector from left top to target point
  const px = localPoint[0] - leftTop[0];
  const py = localPoint[1] - leftTop[1];

  // setup for quadratic equation: at^2 + bt + c = 0
  const a = dyTop * dxMid - dxTop * dyMid;
  const b = px * dyMid - dxTop * dyLeft - py * dxMid + dyTop * dxLeft;
  const c = px * dyLeft - py * dxLeft;

  const eps = 1e-10;
  // default to the midpoint as a safe fallback if no equation can be solved
  let t = 0.5;

  if (Math.abs(a) < eps) {
    // If 'a' is near zero, treat as a linear equation
    if (Math.abs(b) > eps) t = -c / b;
  } else {
    const disc = b * b - 4 * a * c;
    if (disc >= 0) {
      const sqrtDisc = Math.sqrt(disc);
      // calculate both possible solutions
      const t1 = (-b + sqrtDisc) / (2 * a);
      const t2 = (-b - sqrtDisc) / (2 * a);

      if (inRange01(t1) && inRange01(t2)) {
        // choose the solution that is closer to the 0.5 midpoint
        t = Math.abs(t1 - 0.5) <= Math.abs(t2 - 0.5) ? t1 : t2;
      } else if (inRange01(t1)) {
        // only t1 is valid solution
        t = t1;
      } else if (inRange01(t2)) {
        // only t2 is valid solution
        t = t2;
      } else {
        // fallback to prevent undefined behavior when both solutions are invalid
        t = clamp(t1, 0, 1);
      }
    }
  }

  return clamp(t, 0, 1);
};

const distanceToLineSegment = (point: Point, line: [Point, Point]): number => {
  const [x, y] = point;
  const [[x1, y1], [x2, y2]] = line;

  // Vector from line start to point
  const A = x - x1;
  const B = y - y1;
  // Vector of line segment
  const C = x2 - x1;
  const D = y2 - y1;

  // project point into line using dot product
  const dot = A * C + B * D;
  const lenSq = C * C + D * D;

  let percentage = -1;
  if (lenSq !== 0) percentage = dot / lenSq;

  let xx: number;
  let yy: number;

  // clamp line to endpoints
  if (percentage < 0) {
    xx = x1;
    yy = y1;
  } else if (percentage > 1) {
    xx = x2;
    yy = y2;
  } else {
    xx = x1 + percentage * C;
    yy = y1 + percentage * D;
  }

  const dx = x - xx;
  const dy = y - yy;

  return Math.sqrt(dx * dx + dy * dy);
};

export const getCornerLineIndexAtPoint = (
  grid: CornerGridGeometry,
  scenePoint: Point,
  threshold: number,
): number => {
  const cornerLines = grid.cornerLines;
  if (!cornerLines?.length) return -1;

  const localPoint: Point = [scenePoint[0] - grid.x, scenePoint[1] - grid.y];

  let closestIndex = -1;
  let closestDist = threshold;

  for (let i = 0; i < cornerLines.length; i++) {
    const line = cornerLines[i];
    const dist = distanceToLineSegment(localPoint, line);
    if (dist < closestDist) {
      closestDist = dist;
      closestIndex = i;
    }
  }

  return closestIndex;
};

/**
 * Calculate endpoints of a corner line at the given percentage (0–1) within a segment,
 * by linearly interpolating along the top and bottom edges of the segment's bounds.
 */
export const getCornerLineAtPercentage = (
  segmentIndex: number,
  percentage: number,
  grid: CornerGridGeometry,
): [Point, Point] => {
  const { leftTop, rightTop, leftBottom, rightBottom } = getSegmentBoundary(
    grid,
    segmentIndex,
  );

  const topPoint: Point = [
    leftTop[0] + percentage * (rightTop[0] - leftTop[0]),
    leftTop[1] + percentage * (rightTop[1] - leftTop[1]),
  ];
  const bottomPoint: Point = [
    leftBottom[0] + percentage * (rightBottom[0] - leftBottom[0]),
    leftBottom[1] + percentage * (rightBottom[1] - leftBottom[1]),
  ];

  return [topPoint, bottomPoint];
};
